import { useCallback, useEffect, useRef, useState } from 'react';
import './CustomerVisitCard.css';
import ImageWithLoader from '../ImageWithLoader/ImageWithLoader';
import { getImageSrc } from '../../utils/imageUtils';

export default function CustomerVisitCard({
  title,
  image_urls = [],
  location,
  description,
  onPreview,
}) {
  const base = process.env.PUBLIC_URL || '';
  const locationIcon = getImageSrc('location.svg');
  const images = image_urls.length ? image_urls : [''];
  const isPreviewable = Boolean(onPreview);
  const trackRef = useRef(null);
  const itemRefs = useRef([]);
  const [activeIndex, setActiveIndex] = useState(0);
  const activeIndexRef = useRef(0);
  const rafIdRef = useRef(0);
  const [shouldLoadImages, setShouldLoadImages] = useState(() => (
    images.map((_, index) => index === 0)
  ));

  useEffect(() => {
    itemRefs.current = itemRefs.current.slice(0, images.length);
  }, [images.length]);

  useEffect(() => {
    setShouldLoadImages((prev) => images.map((_, index) => prev[index] || index === 0));
  }, [images.length]);

  useEffect(() => {
    if (!images.length) return undefined;
    if (typeof window === 'undefined' || !('IntersectionObserver' in window)) {
      setShouldLoadImages(images.map(() => true));
      return undefined;
    }

    const track = trackRef.current;
    if (!track) {
      setShouldLoadImages(images.map(() => true));
      return undefined;
    }

    const items = itemRefs.current.filter(Boolean);
    if (!items.length) {
      setShouldLoadImages(images.map(() => true));
      return undefined;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          const index = Number(entry.target.dataset.index);
          if (Number.isNaN(index)) return;
          setShouldLoadImages((prev) => {
            if (prev[index]) return prev;
            const next = [...prev];
            next[index] = true;
            return next;
          });
        });
      },
      { root: track, rootMargin: '200px', threshold: 0.01 },
    );

    items.forEach((item) => observer.observe(item));
    return () => observer.disconnect();
  }, [images.length]);

  useEffect(() => {
    activeIndexRef.current = activeIndex;
  }, [activeIndex]);

  const updateActiveIndex = useCallback(() => {
    const track = trackRef.current;
    const items = itemRefs.current.filter(Boolean);
    if (!track || items.length === 0) return;

    const maxScrollLeft = track.scrollWidth - track.clientWidth;
    if (track.scrollLeft <= 1) {
      setActiveIndex(0);
      return;
    }
    if (maxScrollLeft > 0 && track.scrollLeft >= maxScrollLeft - 1) {
      setActiveIndex(items.length - 1);
      return;
    }

    const trackRect = track.getBoundingClientRect();
    const trackCenter = trackRect.left + trackRect.width / 2;

    let closestIndex = 0;
    let closestDistance = Number.POSITIVE_INFINITY;

    items.forEach((item, index) => {
      const rect = item.getBoundingClientRect();
      const itemCenter = rect.left + rect.width / 2;
      const distance = Math.abs(itemCenter - trackCenter);

      if (distance < closestDistance) {
        closestDistance = distance;
        closestIndex = index;
      }
    });

    const currentIndex = activeIndexRef.current;
    const currentItem = items[currentIndex];
    if (currentItem) {
      const rect = currentItem.getBoundingClientRect();
      const currentCenter = rect.left + rect.width / 2;
      const currentDistance = Math.abs(currentCenter - trackCenter);
      const hysteresis = Math.min(24, trackRect.width * 0.08);

      if (currentDistance <= closestDistance + hysteresis) {
        return;
      }
    }

    setActiveIndex((prev) => (prev === closestIndex ? prev : closestIndex));
  }, []);

  const handleScroll = useCallback(() => {
    if (rafIdRef.current) cancelAnimationFrame(rafIdRef.current);
    rafIdRef.current = requestAnimationFrame(updateActiveIndex);
  }, [updateActiveIndex]);

  useEffect(() => {
    handleScroll();
    window.addEventListener('resize', handleScroll);

    return () => {
      if (rafIdRef.current) cancelAnimationFrame(rafIdRef.current);
      window.removeEventListener('resize', handleScroll);
    };
  }, [handleScroll, images.length]);

  return (
    <article className="CustomerVisitCard">
      {title ? <h3 className="CustomerVisitCard__title">{title}</h3> : null}
      <div
        className="CustomerVisitCard__imageTrack"
        ref={trackRef}
        onScroll={handleScroll}
      >
        {images.map((imageUrl, index) => {
          const imageSrc = getImageSrc(imageUrl, { fallback: `${base}/people_default.png` });
          const imageAlt = title ? `${title} visit ${index + 1}` : `Visit ${index + 1}`;
          const handlePreview = () => {
            if (!isPreviewable) return;
            onPreview(imageSrc, imageAlt);
          };
          const handleKeyDown = (event) => {
            if (!isPreviewable) return;
            if (event.key === 'Enter' || event.key === ' ') {
              event.preventDefault();
              handlePreview();
            }
          };

          return (
          <div
            key={`${imageUrl || 'default'}-${index}`}
            className="CustomerVisitCard__imageItem"
            ref={(el) => {
              itemRefs.current[index] = el;
            }}
            data-index={index}
          >
            {shouldLoadImages[index] ? (
              <ImageWithLoader
                wrapperClassName={[
                  'CustomerVisitCard__imageLoader',
                  isPreviewable ? 'CustomerVisitCard__imageLoader--interactive' : '',
                ]
                  .filter(Boolean)
                  .join(' ')}
                className="CustomerVisitCard__image"
                src={imageSrc}
                alt={imageAlt}
                loading="lazy"
                decoding="async"
                fetchPriority="low"
                onClick={handlePreview}
                onKeyDown={handleKeyDown}
                role={isPreviewable ? 'button' : undefined}
                tabIndex={isPreviewable ? 0 : undefined}
              />
            ) : null}
          </div>
          );
        })}
      </div>
      <div className="CustomerVisitCard__metaRow">
        <div className="CustomerVisitCard__indicators" aria-hidden="true">
          {images.map((imageUrl, index) => (
            <span
              key={`${imageUrl || 'default'}-indicator-${index}`}
              className={`CustomerVisitCard__indicator ${index === activeIndex ? 'is-active' : ''}`}
            />
          ))}
        </div>
        {location ? (
          <span className="CustomerVisitCard__location">
            <img
              className="CustomerVisitCard__locationIcon"
              src={locationIcon}
              alt=""
              aria-hidden="true"
            />
            {location}
          </span>
        ) : null}
      </div>
      {description ? (
        <p className="CustomerVisitCard__description">{description}</p>
      ) : null}
    </article>
  );
}
